import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CreditSettlementReport } from '../../api.js';
import { toCredits } from '../../components/AllocationPanel.js';
import { describeError } from '../../errors.js';
import type { AdminHttpClient } from '../../http.js';
import type { ToastMessage } from '../../types.js';

export function CreditSettlementPanel({
  http,
  onToast,
}: {
  http: AdminHttpClient;
  onToast: (toast: ToastMessage) => void;
}) {
  const { t } = useTranslation();
  const [report, setReport] = useState<CreditSettlementReport>();
  const [remainingMonths, setRemainingMonths] = useState(6);
  const [busy, setBusy] = useState(false);
  const fail = useCallback(
    (error: unknown) => onToast({ tone: 'error', message: describeError(error, t) }),
    [onToast, t]
  );

  const load = useCallback(async () => {
    setBusy(true);
    try {
      setReport(await http.creditSettlement({ months: 12, remainingMonths }));
    } catch (error) {
      fail(error);
    } finally {
      setBusy(false);
    }
  }, [fail, http, remainingMonths]);

  useEffect(() => {
    void load();
  }, [load]);

  async function exportCsv() {
    let url: string | undefined;
    let anchor: HTMLAnchorElement | undefined;
    try {
      const csv = await http.creditSettlementExport({ months: 12, remainingMonths });
      url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
      anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'veluga-credit-settlement.csv';
      document.body.append(anchor);
      anchor.click();
    } catch (error) {
      fail(error);
    } finally {
      anchor?.remove();
      if (url) {
        const objectUrl = url;
        setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
      }
    }
  }

  const current = report?.currentMonth;
  const contract = report?.contract;
  return (
    <section className="panel credit-settlement-panel">
      <div className="quota-profiles-heading">
        <h2>{t('creditSettlement.title')}</h2>
        <button type="button" disabled={busy || !report} onClick={() => void exportCsv()}>
          {t('creditSettlement.exportCsv')}
        </button>
      </div>
      <p className="muted">{t('creditSettlement.utcNotice')}</p>
      <label>
        <span>{t('creditSettlement.remainingMonths')}</span>
        <input
          type="number"
          min={1}
          max={120}
          value={remainingMonths}
          onChange={(event) =>
            setRemainingMonths(Math.min(120, Math.max(1, Number(event.target.value) || 1)))
          }
        />
      </label>
      {current && contract ? (
        <div className="metric-grid">
          <article className="metric-card">
            <span>{t('creditSettlement.currentMonth')}</span>
            <strong>
              {current.allocatedCreditMillis === undefined
                ? t('creditSettlement.usedOnly', { used: toCredits(current.usedCreditMillis) })
                : t('creditSettlement.usedOf', {
                    used: toCredits(current.usedCreditMillis),
                    budget: toCredits(current.allocatedCreditMillis),
                    percent: Math.round(current.percentUsed ?? 0),
                  })}
            </strong>
            <small>
              {t('creditSettlement.resetsAt', { at: new Date(current.resetsAt).toLocaleString() })}
            </small>
          </article>
          <article className="metric-card">
            <span>{t('creditSettlement.contractCumulative')}</span>
            <strong>
              {contract.totalBudgetCreditMillis === undefined
                ? t('creditSettlement.usedOnly', { used: toCredits(contract.usedCreditMillis) })
                : t('creditSettlement.usedOf', {
                    used: toCredits(contract.usedCreditMillis),
                    budget: toCredits(contract.totalBudgetCreditMillis),
                    percent: Math.round(contract.percentUsed ?? 0),
                  })}
            </strong>
            {contract.monthlyPaceCreditMillis !== undefined ? (
              <small>
                {t('creditSettlement.pace', {
                  months: contract.remainingMonths,
                  credits: toCredits(contract.monthlyPaceCreditMillis),
                })}
              </small>
            ) : null}
          </article>
        </div>
      ) : null}

      <h3>{t('creditSettlement.monthlyTable')}</h3>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>{t('creditSettlement.month')}</th>
              <th>{t('creditSettlement.activeUsers')}</th>
              <th>{t('creditSettlement.credits')}</th>
            </tr>
          </thead>
          <tbody>
            {(report?.months ?? []).map((row) => (
              <tr key={row.month}>
                <td>{row.month}</td>
                <td>{row.activeUsers}</td>
                <td>{toCredits(row.creditMillis)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3>{t('creditSettlement.freeTierTitle')}</h3>
      <p className="muted">{t('creditSettlement.freeTierNotice')}</p>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>{t('creditSettlement.month')}</th>
              <th>{t('creditSettlement.user')}</th>
              <th>{t('creditSettlement.requests')}</th>
              <th>{t('creditSettlement.tokens')}</th>
              <th>{t('creditSettlement.costUsd')}</th>
              <th>{t('creditSettlement.waivedCredits')}</th>
            </tr>
          </thead>
          <tbody>
            {(report?.freeTier ?? []).map((row) => (
              <tr key={`${row.month}-${row.userId}`}>
                <td>{row.month}</td>
                <td>{row.userId}</td>
                <td>{row.requests}</td>
                <td>{row.inputTokens + row.outputTokens}</td>
                <td>{(row.costMicroUsd / 1_000_000).toFixed(4)}</td>
                <td>{(row.waivedCreditMicros / 1_000_000).toFixed(3)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
